import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const pretty = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return value.length > 260 ? `${value.slice(0, 260)}…` : value;
    }
  }
  if (typeof parsed !== "object") return String(parsed);
  return Object.entries(parsed)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .slice(0, 8)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v).slice(0, 60) : v}`)
    .join(" • ") || "—";
};

export const AuditLog = () => {
  const [filters, setFilters] = useState({
    entity: "", action: "", user_id: "", date_from: "", date_to: "", q: "",
    sensitive_only: false, limit: 100,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== false) params.set(k, v);
    });
    return params.toString();
  }, [filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/audit-logs?${query}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => {
        logError("audit log", e);
        toast.error(apiError(e));
      })
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <div className="mt-4 space-y-3" data-testid="audit-log">
      <div className="surface grid gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
        <Field label="Area">
          <Select value={filters.entity || "all"}
            onValueChange={(v) => set("entity", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All areas</SelectItem>
              {(data?.entities || []).map((e) => (
                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Who">
          <Select value={filters.user_id || "all"}
            onValueChange={(v) => set("user_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              {(data?.actors || []).map((a) => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="From">
          <Input type="date" data-testid="audit-from" value={filters.date_from}
            onChange={(e) => set("date_from", e.target.value)} />
        </Field>
        <Field label="To">
          <Input type="date" data-testid="audit-to" value={filters.date_to}
            onChange={(e) => set("date_to", e.target.value)} />
        </Field>
        <Field label="Search action, name or value">
          <Input data-testid="audit-search" placeholder="salary, approve, Ramesh…"
            value={filters.q} onChange={(e) => set("q", e.target.value)} />
        </Field>
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={filters.sensitive_only} data-testid="audit-sensitive-only"
              onCheckedChange={(v) => set("sensitive_only", v)} />
            Sensitive only
          </label>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={load}
            data-testid="audit-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground" data-testid="audit-count">
        {data ? `${data.items.length} shown of ${data.total} matching entries` : "Loading…"}
      </p>

      <div className="space-y-2">
        {(data?.items || []).map((a) => (
          <div key={a.id} className="surface p-3" data-testid={`audit-row-${a.id}`}>
            <button type="button" className="w-full text-left"
              onClick={() => setOpen(open === a.id ? null : a.id)}
              data-testid={`audit-toggle-${a.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  <span className="font-medium">{a.user_name || "system"}</span>{" "}
                  <span className="text-muted-foreground">{a.action.replace(/_/g, " ")}</span>{" "}
                  <Badge variant="outline" className="text-[10px]">{a.entity}</Badge>
                  {a.ai_source && (
                    <Badge className="ml-1 bg-accent text-accent-foreground text-[9px]">AI</Badge>
                  )}
                </div>
                <span className="font-mono-num text-xs text-muted-foreground">
                  {String(a.created_at).slice(0, 16).replace("T", " ")}
                </span>
              </div>
            </button>
            {open === a.id && (
              <div className="mt-2 grid gap-2 border-t border-border pt-2 sm:grid-cols-2"
                data-testid={`audit-detail-${a.id}`}>
                <div>
                  <div className="overline">Old value</div>
                  <p className="text-xs text-muted-foreground">{pretty(a.old_value)}</p>
                </div>
                <div>
                  <div className="overline">New value</div>
                  <p className="text-xs">{pretty(a.new_value)}</p>
                </div>
                <div className="text-[11px] text-muted-foreground sm:col-span-2">
                  Record id {String(a.entity_id)}
                </div>
              </div>
            )}
          </div>
        ))}
        {data && data.items.length === 0 && (
          <div className="surface p-6 text-center text-sm text-muted-foreground"
            data-testid="audit-empty">
            <ShieldCheck className="mx-auto mb-2 h-5 w-5" />
            No changes match these filters.
          </div>
        )}
      </div>
    </div>
  );
};
